
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/SafeAuthProvider";
import { JournalEntry } from './types';
import { JournalFormValues } from '@/components/journal/form/JournalFormSchema';
import { useSaveJournalEntry } from './useSaveJournalEntry';
import { DifficultyRank } from '@/utils/difficultyRanks';

export const useJournalEntry = (journalPrompts: any) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { entryId } = useParams<{ entryId: string }>();
  const [journalEntry, setJournalEntry] = useState<JournalEntry | null>(null);
  const [isNewEntry, setIsNewEntry] = useState(true);
  const [activeDiceValue, setActiveDiceValue] = useState(0);

  // Function to fetch a journal entry by ID
  const fetchJournalEntry = useCallback(async (id: string) => {
    try {
      const { data, error } = await supabase
        .from("journal_entries")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching journal entry:", error);
        toast({
          title: "Error",
          description: "Failed to fetch journal entry.",
          variant: "destructive",
        });
        return null;
      }

      if (data) {
        setJournalEntry(data);
        setIsNewEntry(false);
        return data;
      } else {
        setIsNewEntry(true);
        return null;
      }
    } catch (error) {
      console.error("Unexpected error fetching journal entry:", error);
      toast({
        title: "Error",
        description: "Failed to fetch journal entry.",
        variant: "destructive",
      });
      return null;
    }
  }, [toast]);

  // Function to get default form values from a journal entry
  const getFormDefaultValues = useCallback((): JournalFormValues => {
    if (journalEntry) {
      return {
        stuck_point: journalEntry.stuck_point || "",
        perception_response: journalEntry.perception_response || "",
        intention_response: journalEntry.intention_response || "",
        next_step_response: journalEntry.next_step_response || "",
        twist_response: journalEntry.twist_response || "",
      };
    }

    return {
      stuck_point: "",
      perception_response: "",
      intention_response: "",
      next_step_response: "",
      twist_response: "",
    };
  }, [journalEntry]);

  // Import the saveJournalEntry function directly
  const { saveJournalEntry, loading } = useSaveJournalEntry();

  // Function to handle saving a journal entry
  const handleSaveEntry = async (values: JournalFormValues, difficultyRank?: DifficultyRank) => {
    if (!activeDiceValue) {
      toast({
        title: "Warning",
        description: "Please roll the dice before saving.",
        variant: "destructive",
      });
      return;
    }

    if (!journalPrompts) {
      toast({
        title: "Error",
        description: "Journal prompts not loaded.",
        variant: "destructive",
      });
      return;
    }

    const success = await saveJournalEntry(
      values,
      activeDiceValue,
      journalPrompts,
      entryId,
      difficultyRank
    );

    if (success) {
      // If save was successful, refetch the journal entry
      if (entryId) {
        fetchJournalEntry(entryId);
      } else {
        // If it's a new entry, navigate to the new entry's page
        navigate(`/journal`);
      }
    }
  };

  // Add the deleteEntry function for JournalEntryPage
  const deleteEntry = async (id: string) => {
    try {
      if (!user) {
        toast({
          title: "Unauthorized",
          description: "You must be logged in to delete journal entries.",
          variant: "destructive",
        });
        return false;
      }

      const { error } = await supabase
        .from("journal_entries")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) {
        console.error("Error deleting journal entry:", error);
        toast({
          title: "Error",
          description: "Failed to delete journal entry.",
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Journal entry deleted successfully.",
      });

      // Navigate to the journal history page
      navigate("/history");
      return true;
    } catch (error) {
      console.error("Unexpected error deleting journal entry:", error);
      toast({
        title: "Error",
        description: "Failed to delete journal entry.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    journalEntry,
    isNewEntry,
    activeDiceValue,
    setActiveDiceValue,
    fetchJournalEntry,
    getFormDefaultValues,
    handleSaveEntry,
    entryId,
    setIsNewEntry,
    setJournalEntry,
    deleteEntry
  };
};
